import * as THREE from 'three';
import { trackLoad } from '@/core/loadTracker';
import { modelLoader } from '@/render/ModelLoader';
import { applyLandmarkTextures } from './texturing';

/**
 * landmarks/model.ts — load a hand-built Blender hero landmark (.glb in
 * public/models/landmarks/) and normalise it to the landmark convention: base on
 * the ground (y=0), centred in X/Z, scaled so its HEIGHT matches `height` world
 * units, facing local +Z. Returns the group SYNCHRONOUSLY (placed immediately by
 * Landmarks.buildLandmark); the mesh streams in and pops into place once decoded.
 *
 * Falls back to nothing visible if the asset 404s — the city still loads. The glTF
 * is parsed once and cloned per use (the same cache the cars use), so a landmark
 * that appears twice is cheap.
 */
const BASE = import.meta.env.BASE_URL;

export interface LandmarkModelOpts {
  /** Target height in world units (the model is scaled uniformly to hit it). */
  height?: number;
  /** OR target X-width — used for drive-through gateways (placement 'span') so the
   *  opening reliably clears the road; pass 2*halfWidth + margin. Wins over height. */
  width?: number;
  /** Extra yaw in degrees, if the authored front doesn't face +Z. */
  yawDeg?: number;
  /** Skip shadow casting (huge distant landmarks rarely need it). */
  noShadow?: boolean;
  /** Hide every mesh primitive using these MATERIAL names (M97 — surgical:
   *  e.g. the Galleria's interior 'win' window wall stands ACROSS the drive-
   *  through hall; dropping the primitive clears the road without remodelling). */
  hideMats?: string[];
  /** Multiply emissiveIntensity on ALL emissive materials (M97 — the 'win' window
   *  slabs on drive-through gateways bloomed to a solid white wall that read
   *  as a blocked road; ~0.3 reads as softly lit glass instead). */
  emissiveScale?: number;
  /** GS6 — per-MATERIAL-name emissive multipliers; wins over `emissiveScale` for
   *  the named materials, and materials NOT named here keep `emissiveScale` (or
   *  stay untouched when no global is given). This is how a def dims its blown
   *  'win' slabs without dulling the same model's gold cross/crescent, or runs
   *  different levels per surface (Empire State strips vs crown). Same
   *  userData-baseline discipline as emissiveScale — never compounds on reload. */
  emissiveScaleMats?: Record<string, number>;
  /** GS6 — per-MATERIAL-name emissive COLOUR override (hex). Used to WARM a
   *  blown emissive (floodlit limestone should glow gold, not pink-white).
   *  An absolute set, so it is idempotent across city reloads even though the
   *  loader cache SHARES materials between clones. */
  emissiveTintMats?: Record<string, number>;
  /** GS6 — per-MATERIAL-name BASE colour override (hex, absolute set). The
   *  M43-era models bake their window slabs with a PALE CREAM base (~#fff3cb),
   *  so under scene light the panes render near-white even at emissive 0 —
   *  MEASURED: cutting belvedere's win emissive 1.0 → 0.35 moved the rendered
   *  core only 247→242. The Apollo (the series quality bar) paints window
   *  glass DARK (#252b37) and lets a warm emissive carry the light; this
   *  override retrofits that recipe def-side: dark pane + emissiveTintMats +
   *  emissiveScaleMats = warm lit panes instead of blown slabs. */
  colorMats?: Record<string, number>;
  /** Hide specific NODES by exact name (e.g. a text object baked lying flat). */
  hideNodes?: string[];
  /** Render these materials double-sided (M97 — facade-shell models read
   *  hollow from behind/inside a drive-through; this closes them). */
  doubleSideMats?: string[];
  /** GS6 — skip the triplanar applyLandmarkTextures pass entirely. REQUIRED for
   *  any externally-sourced GLB that ships its OWN baked PBR textures: the
   *  texturing classifier keys on material NAMES, and a matched name (e.g.
   *  "brick_wall") gets the shared triplanar albedo MULTIPLIED over the baked
   *  map — a mongrel, not a clean override. Two integration paths for imports:
   *    (a) set `skipTexturing: true` — the honest per-model contract, or
   *    (b) name the baked materials to MISS the classifier regexes in
   *        texturing.ts (e.g. 'baked_01', 'atlas') — unmatched names are left
   *        completely alone (emissive / metallic / glassy mats auto-skip too).
   *  Every other opt (hideMats / emissiveScale / tints / decorate) still applies
   *  to a skipped model — the emissive baseline mechanism is independent of the
   *  texturing pass. */
  skipTexturing?: boolean;
  /** Add extra meshes INSIDE the model once it streams in. Children are in the
   *  model's NATIVE coordinate space — the normalisation scale applies to them
   *  automatically (used for the Dakar arch's DAKAR boards, M97). */
  decorate?: (model: THREE.Group) => void;
}

/** The Apollo window language (apollo.glb is the series quality bar):
 *  near-black blue glass base + a saturated warm-gold glow. GLOW was tuned
 *  LIVE against NYC's dusk (GS6): the Apollo's own cream #e7c592 rendered as
 *  flat cardboard-tan on big panes — the saturated gold is what reads "lit"
 *  and matches the M41 city-facade windows. sRGB hex — both Color.set calls
 *  convert to the linear working space. */
export const WINDOW_GLASS_BASE = 0x252b37;
export const WINDOW_GLASS_GLOW = 0xffc36b;

/** GS6 — retrofit the Apollo window recipe onto a blown 'win' slab material:
 *  dark glass pane, warm glow at `scale` of the baked intensity. Spread into
 *  a def's opts: `{ height: 24, ...windowGlass('win', 0.85) }`. Pick `scale`
 *  by the city's light — night cities keep windows alive (~0.8), daylight
 *  cities read them as tinted glass (~0.35-0.5). */
export function windowGlass(
  matName: string,
  scale: number,
): Pick<LandmarkModelOpts, 'colorMats' | 'emissiveTintMats' | 'emissiveScaleMats'> {
  return {
    colorMats: { [matName]: WINDOW_GLASS_BASE },
    emissiveTintMats: { [matName]: WINDOW_GLASS_GLOW },
    emissiveScaleMats: { [matName]: scale },
  };
}

/** The post-normalisation dressing pass: shadow flags, hide/emissive/side
 *  surgery, then the triplanar texturing (unless skipTexturing). Split out of
 *  the load promise so the option semantics are unit-testable headless. */
export function dressLandmarkModel(model: THREE.Object3D, opts: LandmarkModelOpts): void {
  if (!opts.noShadow) {
    model.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
  }
  if (
    opts.hideMats ||
    opts.emissiveScale != null ||
    opts.emissiveScaleMats ||
    opts.emissiveTintMats ||
    opts.colorMats ||
    opts.hideNodes ||
    opts.doubleSideMats
  ) {
    const hide = new Set(opts.hideMats ?? []);
    const hideN = new Set(opts.hideNodes ?? []);
    const twoSided = new Set(opts.doubleSideMats ?? []);
    model.traverse((o) => {
      if (hideN.has(o.name)) o.visible = false;
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      for (const mat of mats as THREE.MeshStandardMaterial[]) {
        // Hide at the MATERIAL level: a mesh can carry a material ARRAY
        // (the Gateway's basalt+win share one mesh) — hiding the mesh
        // would take the whole landmark down with it.
        if (hide.has(mat.name)) mat.visible = false;
        if (twoSided.has(mat.name)) mat.side = THREE.DoubleSide;
        if (hide.has(mat.name)) continue;
        const scale = opts.emissiveScaleMats?.[mat.name] ?? opts.emissiveScale;
        if (
          scale != null &&
          mat.emissive &&
          (mat.emissive.r > 0 || mat.emissive.g > 0 || mat.emissive.b > 0)
        ) {
          // clone(true) SHARES materials with the loader cache — scale from
          // a stored baseline so reloading the city never compounds it.
          const ud = mat.userData as { baseEmissiveIntensity?: number };
          ud.baseEmissiveIntensity ??= mat.emissiveIntensity;
          mat.emissiveIntensity = ud.baseEmissiveIntensity * scale;
        }
        const tint = opts.emissiveTintMats?.[mat.name];
        if (tint != null && mat.emissive) mat.emissive.set(tint);
        const col = opts.colorMats?.[mat.name];
        if (col != null) mat.color.set(col);
      }
    });
  }
  // Triplanar brick/stone/wood by material role — gated so a fully-baked
  // external GLB renders exactly as authored (see skipTexturing above).
  if (!opts.skipTexturing) applyLandmarkTextures(model);
}

export function landmarkModel(url: string, opts: LandmarkModelOpts): THREE.Group {
  const group = new THREE.Group();
  // Registered with the load tracker (loading round): the race-start warm-up
  // waits for every landmark to land before it precompiles shaders, so a
  // model can no longer pop in — and compile — mid-race.
  trackLoad(modelLoader.load(BASE + url))
    .then((model) => {
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      const scale =
        opts.width && size.x > 1e-3
          ? opts.width / size.x
          : size.y > 1e-3
            ? (opts.height ?? 20) / size.y
            : 1;
      model.scale.setScalar(scale);
      // Centre X/Z on the origin, drop the base to y=0.
      model.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
      // Rotation about Y through the centred/grounded origin keeps it grounded.
      if (opts.yawDeg) model.rotation.y = THREE.MathUtils.degToRad(opts.yawDeg);

      dressLandmarkModel(model, opts);
      opts.decorate?.(model);
      group.add(model);
    })
    .catch((e: unknown) => console.warn('[landmark] failed to load', url, e));
  return group;
}
